import React, { useState } from "react";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, ArrowRight, FileText } from "lucide-react";
import { toast } from "sonner";
import ISOAgreementModal from "./ISOAgreementModal";

const REVENUES = [
    "$10,000 – $20,000",
    "$20,000 – $50,000",
    "$50,000 – $100,000",
    "$100,000 – $250,000",
    "$250,000 – $500,000",
    "$500,000+",
];
const FUNDING = [
    "$10,000 – $50,000",
    "$50,000 – $100,000",
    "$100,000 – $250,000",
    "$250,000 – $500,000",
    "$500,000 – $1,000,000",
    "$1,000,000 – $3,000,000",
];

export default function LeadForm({ defaultAudience = "sbo", compact = false, simple = false }) {
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [createdLead, setCreatedLead] = useState(null);
    const [agreementOpen, setAgreementOpen] = useState(false);
    const [form, setForm] = useState({
        name: "",
        phone: "",
        phone_type: "Mobile",
        email: "",
        business_name: "",
        country: "United States",
        monthly_revenue: "",
        funding_amount: "",
        audience_type: defaultAudience,
        message: "",
        tcpa_consent: false,
    });

    const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    const onCheck = (k) => (e) => setForm({ ...form, [k]: e.target.checked });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.tcpa_consent) {
            toast.error("Please agree to the Terms & Privacy Policy to continue.");
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post("/leads", form);
            setCreatedLead(data);
            setSubmitted(true);
            toast.success("Application received. We'll contact you shortly.");
            // Auto-open ISO agreement for ISO submissions
            if (form.audience_type === "iso") {
                setTimeout(() => setAgreementOpen(true), 400);
            }
        } catch (err) {
            const detail = err?.response?.data?.detail || "Submission failed. Please try again.";
            toast.error(typeof detail === "string" ? detail : "Submission failed.");
        } finally {
            setLoading(false);
        }
    };

    if (submitted) {
        const isISO = form.audience_type === "iso";
        return (
            <>
                <div className="bg-white border-2 border-[#0A0A0A] p-8 md:p-10 text-center" data-testid="lead-form-success">
                    <CheckCircle2 className="mx-auto text-[#FF6B1A]" size={56} />
                    <h3 className="font-display text-3xl font-bold mt-4">You're in.</h3>
                    <p className="text-black/70 mt-3 max-w-md mx-auto">
                        {isISO
                            ? "Complete the ISO Partner Agreement below to activate your account and start earning."
                            : "A funding specialist will reach out within one business hour."}{" "}
                        For urgent deals call{" "}
                        <a href="tel:5167305656" className="text-[#FF6B1A] font-semibold">516-730-5656</a>.
                    </p>
                    {isISO && (
                        <button
                            type="button"
                            onClick={() => setAgreementOpen(true)}
                            className="btn-orange mt-6 mx-auto"
                            data-testid="open-iso-agreement"
                        >
                            <FileText size={18} /> Complete ISO Agreement
                        </button>
                    )}
                </div>
                <ISOAgreementModal
                    open={agreementOpen}
                    onClose={() => setAgreementOpen(false)}
                    leadId={createdLead?.id || ""}
                    prefill={createdLead || form}
                />
            </>
        );
    }

    const inputClass =
        "w-full h-12 px-4 bg-white border-2 border-black/10 focus:border-[#FF6B1A] outline-none rounded-none text-[15px] transition-colors";

    return (
        <form onSubmit={submit} className="bg-white border-2 border-[#0A0A0A] p-6 md:p-8 shadow-[8px_8px_0_0_#FF6B1A]" data-testid="lead-form">
            {!compact && (
                <div className="mb-6">
                    <div className="eyebrow">{simple ? "ISO Partner Inquiry" : (form.audience_type === "iso" ? "ISO / Broker" : "Get Funded")}</div>
                    <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight mt-1">
                        {simple ? "Become a Partner" : (form.audience_type === "iso" ? "Submit a deal and get paid" : "See how much you qualify for")}
                    </h3>
                    <p className="text-sm text-black/60 mt-2">
                        {simple
                            ? "Quick intro — we'll reach out within one business hour."
                            : (form.audience_type === "iso"
                                ? "Direct underwriter access. Decisions in 4 hours. Commissions paid the next business day."
                                : "No fees. No credit pull to apply. 60-second form.")}
                    </p>
                </div>
            )}

            {!simple && (
                <div className="flex gap-2 mb-5 border border-black/10 p-1">
                    {[
                        { v: "sbo", label: "Small Business" },
                        { v: "iso", label: "ISO / Broker" },
                    ].map((t) => (
                        <button
                            key={t.v}
                            type="button"
                            data-testid={`audience-toggle-${t.v}`}
                            onClick={() => setForm({ ...form, audience_type: t.v })}
                            className={`flex-1 h-10 text-sm font-semibold transition ${
                                form.audience_type === t.v ? "bg-[#0A0A0A] text-white" : "bg-transparent text-black/70"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input data-testid="lead-name" required placeholder="Your name" value={form.name} onChange={onChange("name")} className={inputClass} />
                <input data-testid="lead-business" required={simple || form.audience_type === "iso"} placeholder="Business name" value={form.business_name} onChange={onChange("business_name")} className={inputClass} />
                <input data-testid="lead-phone" required placeholder="Phone number" value={form.phone} onChange={onChange("phone")} className={`${inputClass} ${simple ? "" : ""}`} />
                {!simple && (
                    <select data-testid="lead-phone-type" value={form.phone_type} onChange={onChange("phone_type")} className={inputClass}>
                        <option>Mobile</option>
                        <option>Landline</option>
                    </select>
                )}
                <input data-testid="lead-email" required type="email" placeholder="Email address" value={form.email} onChange={onChange("email")} className={`${inputClass} ${simple || form.audience_type === "iso" ? "" : "md:col-span-2"}`} />
                {!simple && form.audience_type !== "iso" && (
                    <>
                        <select data-testid="lead-country" value={form.country} onChange={onChange("country")} className={inputClass}>
                            <option>United States</option>
                            <option>Canada</option>
                            <option>Australia</option>
                        </select>
                        <select data-testid="lead-revenue" required value={form.monthly_revenue} onChange={onChange("monthly_revenue")} className={inputClass}>
                            <option value="">Monthly revenue</option>
                            {REVENUES.map((r) => <option key={r}>{r}</option>)}
                        </select>
                        <select data-testid="lead-funding" required value={form.funding_amount} onChange={onChange("funding_amount")} className={`${inputClass} md:col-span-2`}>
                            <option value="">Funding amount needed</option>
                            {FUNDING.map((r) => <option key={r}>{r}</option>)}
                        </select>
                    </>
                )}
            </div>

            <label className="flex items-start gap-3 mt-5 text-xs text-black/65 leading-relaxed cursor-pointer" data-testid="lead-tcpa-label">
                <input
                    type="checkbox"
                    checked={form.tcpa_consent}
                    onChange={onCheck("tcpa_consent")}
                    className="mt-0.5 w-4 h-4 accent-[#FF6B1A]"
                    data-testid="lead-tcpa-checkbox"
                />
                <span>
                    By checking this box, you agree to receive text messages (e.g., payment reminders, 2FA, account
                    notifications, alerts, customer service) from <strong>Lended</strong> at the cell number
                    used when signing up. Consent is not a condition of any purchase. Reply STOP to unsubscribe, HELP for
                    help. Message & data rates may apply. Message frequency varies. I have read and agree with the{" "}
                    <a href="/terms" className="text-[#FF6B1A] underline">Terms and Conditions</a> &{" "}
                    <a href="/privacy" className="text-[#FF6B1A] underline">Privacy Policy</a>.
                </span>
            </label>

            <button
                type="submit"
                disabled={loading}
                className="btn-orange w-full justify-center mt-5 text-base disabled:opacity-60"
                data-testid="lead-form-submit"
            >
                {loading ? <Loader2 className="animate-spin" size={18} /> : <ArrowRight size={18} />}
                {loading
                    ? "Submitting..."
                    : (simple
                        ? "Become a Partner"
                        : (form.audience_type === "iso" ? "Submit Deal" : "Get My Funding Quote"))}
            </button>
        </form>
    );
}
